import React, { useEffect, useRef, useState } from 'react';
import { api } from '../api';

export const QuoteItemPicker = ({ line, onReplace, onClose }) => {
  const [query, setQuery] = useState(line.code || '');
  const [hits, setHits] = useState(null);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const request = useRef(0);
  useEffect(() => () => { request.current += 1; }, []);
  async function search() {
    if (!query.trim()) return;
    const current = ++request.current;
    setBusy(true); setError(''); setHits(null); setSelected(null);
    try {
      const data = await api.nsSearch(query.trim());
      if (current === request.current) setHits(data.items || []);
    } catch (e) { if (current === request.current) setError(e.response?.data?.detail || 'NetSuite search failed. Try again; the current item has not changed.'); }
    finally { if (current === request.current) setBusy(false); }
  }
  async function replace() {
    if (!selected) return;
    setBusy(true); setError('');
    let cost = '';
    if (selected.id) {
      try {
        const info = await api.nsItemFields(selected.id);
        const price = info.vendor?.purchase_price;
        if (price != null) cost = String(price);
      } catch (_) { /* A new cost must be entered manually, never reuse the old item's cost. */ }
    }
    onReplace({ code: selected.itemid, displayName: selected.displayname || selected.itemid,
      desc: selected.description || selected.salesdescription || '', nsItemId: selected.id || null,
      nsFound: !!selected.id, nsInactive: selected.isinactive === 'T', cost });
  }
  return <div className="modal-overlay" data-testid="quote-item-picker-overlay" onClick={() => { if (!busy) onClose(); }}>
    <div className="modal quote-item-picker" role="dialog" aria-modal="true" aria-labelledby="quote-item-picker-title" data-testid="quote-item-picker" onClick={e => e.stopPropagation()} onKeyDown={e => { if (e.key === 'Escape' && !busy) onClose(); }}>
      <div className="quote-picker-header"><h3 id="quote-item-picker-title">Change item</h3><button type="button" className="modal-close" aria-label="Close item picker" data-testid="quote-item-picker-close" disabled={busy} onClick={onClose}>×</button></div>
      <div className="quote-picker-current" data-testid="quote-item-current">Current item: <strong>{line.code || '(unnamed)'}</strong></div>
      <div className="pc-quote-addrow">
        <input className="input" autoFocus data-testid="quote-item-search" aria-label="Search replacement item" value={query} disabled={busy}
          onChange={e => { request.current += 1; setBusy(false); setQuery(e.target.value); setHits(null); setSelected(null); setError(''); }}
          onKeyDown={e => { if (e.key === 'Enter' && !busy) search(); }} />
        <button type="button" className="btn" data-testid="quote-item-search-button" disabled={busy || !query.trim()} onClick={search}>{busy ? 'Searching…' : 'Search'}</button>
      </div>
      {error && <div className="quote-error" role="alert" data-testid="quote-item-search-error">{error}</div>}
      {hits && <div className="nsp-search-results" data-testid="quote-item-results">
        {hits.length === 0 && <div data-testid="quote-item-no-results">No NetSuite items found.</div>}
        {hits.map(hit => <button type="button" className={'nsp-search-hit' + (String(selected?.id) === String(hit.id) ? ' selected' : '')} data-testid={'quote-item-option-' + hit.id} key={hit.id} disabled={busy} onClick={() => setSelected(hit)}>
          <strong>{hit.itemid}{hit.isinactive === 'T' ? ' · INACTIVE' : ''}</strong><span>{hit.displayname}</span>
        </button>)}
      </div>}
      <button type="button" className="btn btn-sm" data-testid="quote-item-generic" disabled={busy || !query.trim()} onClick={() => setSelected({ itemid: query.trim(), displayname: query.trim() })}>Use “{query.trim() || '…'}” as a generic item</button>
      {selected && <div className="quote-picker-selection" data-testid="quote-item-selection">
        <strong>{selected.itemid}</strong>
        <p className="quote-description-text" data-testid="quote-item-description">{selected.description || selected.salesdescription || ''}</p>
        {selected.isinactive === 'T' && <div className="quote-error" data-testid="quote-item-inactive-warning">This item is inactive. NetSuite will reject it until it is reactivated or replaced.</div>}
      </div>}
      <div className="quote-picker-actions"><button type="button" className="btn" data-testid="quote-item-cancel" disabled={busy} onClick={onClose}>Cancel</button>
        <button type="button" className="btn btn-primary" data-testid="quote-item-replace" disabled={!selected || busy} onClick={replace}>{busy ? 'Loading…' : 'Replace item'}</button></div>
    </div>
  </div>;
};